import { Stack } from '@chakra-ui/react';
import { UserMinusIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { Bleed, Card } from '@main/complykit';
import {
  GetOrganizationsByUserIdQuery,
  useGetOrganizationsByUserIdQuery,
} from '@main/graphql/queries/GetOrganizationsByUserId.generated';
import {
  actionHelper,
  BulkSelectionToolbarPortal,
  createColumnHelper,
  Table,
  useAlertDialog,
  useDrawer,
} from '@main/ui';
import { Row } from '@tanstack/react-table';
import { useMemo, useRef } from 'react';

import { getRoleTagConfig, STATUSES } from '../organization/constants';
import { useUpdateUserStateAction } from './disable-user-action';
import { useUsersServerPaginatedQuery } from './manage-users';

const tableId = 'organizations';

export function OrganizationsTab({ userId }: { userId: string }) {
  const bulkSelectionToolbarPortalRef = useRef<HTMLElement>(null);
  const { columns, onRowClick } = useOrgsTableColumns({ userId });
  const { data } = useGetOrganizationsByUserIdQuery(
    { userId },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const organizations = data?.organization_users || [];

  return (
    <Stack spacing={6}>
      <Card>
        <Bleed>
          <Table
            id={tableId}
            data={organizations}
            columns={columns}
            itemName={{
              singular: 'organization',
              plural: 'organizations',
            }}
            bulkSelectionToolbarPortalRef={bulkSelectionToolbarPortalRef}
            onRowClick={onRowClick}
          />
        </Bleed>
      </Card>

      <BulkSelectionToolbarPortal ref={bulkSelectionToolbarPortalRef} />
    </Stack>
  );
}

type Organization = GetOrganizationsByUserIdQuery['organization_users'][number];

function useOrgsTableColumns({ userId }: { userId: string }) {
  const { openDialog } = useAlertDialog();
  const drawer = useDrawer();

  const { refetch: refetchOrganizations } = useGetOrganizationsByUserIdQuery({ userId });
  const { refetchQuery: refetchUsers } = useUsersServerPaginatedQuery();

  const updateUserState = useUpdateUserStateAction(() => {
    refetchOrganizations();
    refetchUsers();
  });

  return useMemo(() => {
    const columnHelper = createColumnHelper<Organization>();

    return {
      onRowClick: (row: Row<Organization>) =>
        drawer.open({ entity: 'organization', entityId: row.original.organization.id }),
      columns: [
        columnHelper.columns.status({
          id: 'status',
          header: 'Status',
          accessorFn: ({ disabled }) => {
            const statusMeta = disabled ? STATUSES.disabled : STATUSES.active;

            return {
              value: statusMeta.value,
              tone: statusMeta.tone,
            };
          },
          size: 120,
          enableGlobalFilter: true,
          enableColumnFilter: true,
        }),
        columnHelper.columns.text({
          id: 'organization',
          header: 'Organization',
          accessorFn: ({ organization }) => organization.name,
          enableGlobalFilter: true,
          enableColumnFilter: true,
        }),
        columnHelper.columns.tag({
          id: 'type',
          header: 'Org type',
          isMulti: true,
          accessorFn: ({ organization }) => {
            return [
              organization.parent_organization_id
                ? { value: 'Sub-Org', colorScheme: 'yellow' }
                : { value: 'Main-Org', colorScheme: 'purple' },
            ];
          },
          size: 120,
          enableColumnFilter: true,
          enableGlobalFilter: true,
        }),
        columnHelper.columns.tag({
          id: 'role',
          header: 'Role',
          accessorFn: ({ role }) => getRoleTagConfig(role),
          size: 120,
          enableGlobalFilter: true,
          enableColumnFilter: true,
        }),
        columnHelper.columns.actions({
          menuActions: ({ row }) => [
            actionHelper.menuActionItem({
              icon: row.original.disabled ? UserPlusIcon : UserMinusIcon,
              label: row.original.disabled ? 'Enable' : 'Disable',
              onClick: () =>
                row.original.disabled
                  ? updateUserState({
                      userIds: userId,
                      organizationIds: row.original.organization.id,
                      disabled: false,
                    })
                  : openDialog({
                      dialogHeader: 'Disable user',
                      dialogContent: 'Are you sure you want to disable this user?',
                      confirmAction: {
                        children: 'Disable',
                        onClick: () =>
                          updateUserState({
                            userIds: userId,
                            organizationIds: row.original.organization.id,
                            disabled: true,
                          }),
                      },
                    }),
            }),
          ],
          meta: {
            bulkActions: [
              (rows) => {
                const isUserDisabled = rows.some((row) => row.original.disabled);

                if (!isUserDisabled) {
                  return;
                }

                return actionHelper.menuActionItem({
                  icon: UserPlusIcon,
                  label: 'Enable',
                  onClick: () =>
                    updateUserState({
                      userIds: userId,
                      organizationIds: rows.map((row) => row.original.organization.id),
                      disabled: false,
                    }),
                });
              },
              (rows) => {
                const isUserEnabled = rows.some((row) => !row.original.disabled);

                if (!isUserEnabled) {
                  return;
                }
                return actionHelper.menuActionItem({
                  icon: UserMinusIcon,
                  label: 'Disable',
                  onClick: () =>
                    openDialog({
                      dialogHeader: 'Disable users',
                      dialogContent: 'Are you sure you want to disable selected users?',
                      confirmAction: {
                        children: 'Disable',
                        onClick: () =>
                          updateUserState({
                            userIds: userId,
                            organizationIds: rows.map((row) => row.original.organization.id),
                            disabled: true,
                          }),
                      },
                    }),
                });
              },
            ],
          },
        }),
      ],
    };
  }, [drawer, openDialog, updateUserState, userId]);
}
